import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, LogOut, Calendar, Users, Scissors, Settings as SettingsIcon,
  Trash2, Pencil, Plus, Check, X, Clock, Ban, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { priceLabel, to12h, prettyDate, buildDays } from "@/lib/format";
import { Logo } from "@/components/Logo";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("am_token"));
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then(() => setChecking(false)).catch(() => {
      localStorage.removeItem("am_token"); setToken(null); setChecking(false);
    });
  }, [token]);

  const logout = () => { localStorage.removeItem("am_token"); setToken(null); };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-obsidian-900 text-gold"><Loader2 className="animate-spin" /></div>;
  }
  if (!token) return <Login onLogin={(t) => { localStorage.setItem("am_token", t); setToken(t); }} />;
  return <Dashboard onLogout={logout} />;
}

/* ---------------- Login ---------------- */
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success("Welcome back");
      onLogin(data.token);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-obsidian-900 px-5">
      <div className="grain" />
      <motion.form
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm rounded-3xl border border-gold/20 bg-obsidian-card p-8 shadow-2xl"
        data-testid="admin-login-form"
      >
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <Logo />
          <div className="mt-2 flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 bg-gold-soft text-gold">
            <Lock size={20} />
          </div>
          <h1 className="font-display text-2xl font-bold text-white">Owner Portal</h1>
          <p className="text-xs text-gray-400">Sign in to manage your barbershop</p>
        </div>
        <div className="space-y-3">
          <input
            data-testid="admin-email-input"
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" required
            className="w-full rounded-xl border border-white/10 bg-obsidian-800 px-4 py-3 text-white outline-none focus:border-gold"
          />
          <input
            data-testid="admin-password-input"
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" required
            className="w-full rounded-xl border border-white/10 bg-obsidian-800 px-4 py-3 text-white outline-none focus:border-gold"
          />
          <button
            data-testid="admin-login-btn"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gold py-3 font-bold text-obsidian-900 transition-colors duration-200 hover:bg-gold-hover disabled:opacity-60"
          >
            {loading && <Loader2 className="animate-spin" size={16} />} Sign In
          </button>
        </div>
        <a href="/" className="mt-5 block text-center text-xs text-gray-500 hover:text-gold">← Back to website</a>
      </motion.form>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
const TABS = [
  { id: "appointments", label: "Appointments", icon: Calendar },
  { id: "barbers", label: "Barbers", icon: Users },
  { id: "services", label: "Services", icon: Scissors },
  { id: "hours", label: "Hours & Blocks", icon: Clock },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("appointments");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, [tab]);

  return (
    <div className="min-h-screen bg-obsidian-900 text-white">
      <header className="sticky top-0 z-40 border-b border-gold/20 bg-obsidian-900/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <Logo />
          <button data-testid="admin-logout-btn" onClick={onLogout} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-gray-300 hover:border-gold hover:text-gold">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8">
        {/* Stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total Bookings" value={stats?.total} />
          <StatCard label="Today" value={stats?.today} />
          <StatCard label="Upcoming" value={stats?.upcoming} />
          <StatCard label="Whish Pending" value={stats?.pending_whish} highlight />
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                tab === t.id ? "border-gold bg-gold text-obsidian-900" : "border-white/10 text-gray-300 hover:border-gold/50"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "appointments" && <Appointments onChange={loadStats} />}
        {tab === "barbers" && <Barbers />}
        {tab === "services" && <Services />}
        {tab === "hours" && <HoursBlocks />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, highlight }) => (
  <div className={`rounded-2xl border p-5 ${highlight ? "border-gold/40 bg-gold-soft" : "border-white/10 bg-obsidian-card"}`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{label}</div>
    <div className="mt-1 font-display text-3xl font-bold text-gold">{value ?? "—"}</div>
  </div>
);

/* ---------- Appointments ---------- */
function Appointments({ onChange }) {
  const [scope, setScope] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const rate = settings?.exchange_rate || 89000;

  const load = () => {
    setLoading(true);
    api.get("/admin/appointments", { params: { scope } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [scope]);
  useEffect(() => {
    api.get("/barbers", { params: { all: true } }).then((r) => setBarbers(r.data));
    api.get("/services", { params: { all: true } }).then((r) => setServices(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
  }, []);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/appointments/${id}`, patch);
      toast.success("Updated");
      load(); onChange?.();
    } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this appointment permanently?")) return;
    try { await api.delete(`/admin/appointments/${id}`); toast.success("Deleted"); load(); onChange?.(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          {["upcoming", "today", "past"].map((s) => (
            <button key={s} data-testid={`appt-scope-${s}`} onClick={() => setScope(s)}
              className={`rounded-full px-4 py-1.5 text-sm capitalize ${scope === s ? "bg-gold text-obsidian-900" : "border border-white/10 text-gray-300"}`}>
              {s}
            </button>
          ))}
        </div>
        <button onClick={load} className="flex items-center gap-1 text-sm text-gray-400 hover:text-gold" data-testid="appt-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-gold"><Loader2 className="mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-obsidian-card p-8 text-center text-gray-400" data-testid="appt-empty">No appointments here.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-2xl border border-white/10 bg-obsidian-card p-4" data-testid={`appt-row-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg font-bold text-white">{a.customer_name}</span>
                    {a.status === "cancelled" && <Badge tone="red">Cancelled</Badge>}
                  </div>
                  <a href={`tel:${a.customer_phone}`} className="font-mono text-sm text-gold">{a.customer_phone}</a>
                  <div className="mt-1 text-sm text-gray-300">{a.service_name} · with {a.barber_name}</div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-400">
                    <Calendar size={13} /> {prettyDate(a.date)} · {to12h(a.time)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-bold text-gold">${a.price_usd}</div>
                  <div className="mt-1"><PaymentBadge a={a} /></div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/5 pt-3">
                {a.payment_method === "whish" && (
                  <span className="font-mono text-xs text-gray-400">Ref: {a.whish_reference || "—"}</span>
                )}
                {a.payment_method === "whish" && a.payment_status !== "paid" && (
                  <button data-testid={`appt-verify-${a.id}`} onClick={() => update(a.id, { payment_status: "paid" })}
                    className="flex items-center gap-1 rounded-full border border-green-500/40 px-3 py-1 text-xs text-green-400 hover:bg-green-500/10">
                    <Check size={13} /> Mark Paid
                  </button>
                )}
                <button data-testid={`appt-edit-${a.id}`} onClick={() => setEditing(a)}
                  className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-gray-300 hover:border-gold hover:text-gold">
                  <Pencil size={13} /> Edit
                </button>
                {a.status !== "cancelled" && (
                  <button data-testid={`appt-cancel-${a.id}`} onClick={() => update(a.id, { status: "cancelled" })}
                    className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-gray-300 hover:border-red-500 hover:text-red-400">
                    <Ban size={13} /> Cancel
                  </button>
                )}
                <button data-testid={`appt-delete-${a.id}`} onClick={() => remove(a.id)}
                  className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-gray-300 hover:border-red-500 hover:text-red-400">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditAppointment appt={editing} barbers={barbers} services={services}
          onClose={() => setEditing(null)}
          onSave={(patch) => { update(editing.id, patch); setEditing(null); }} />
      )}
    </div>
  );
}

const PaymentBadge = ({ a }) => {
  if (a.payment_method === "pay_at_shop") return <Badge tone="gray">Pay at shop</Badge>;
  if (a.payment_status === "paid") return <Badge tone="green">Whish · Paid</Badge>;
  return <Badge tone="gold">Whish · Pending</Badge>;
};

const Badge = ({ children, tone = "gray" }) => {
  const tones = {
    gray: "border-white/15 text-gray-300",
    gold: "border-gold/40 text-gold",
    green: "border-green-500/40 text-green-400",
    red: "border-red-500/40 text-red-400",
  };
  return <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
};

function EditAppointment({ appt, barbers, services, onClose, onSave }) {
  const [f, setF] = useState({
    barber_id: appt.barber_id, service_id: appt.service_id,
    date: appt.date, time: appt.time,
    customer_name: appt.customer_name, customer_phone: appt.customer_phone,
    payment_status: appt.payment_status,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const times = Array.from({ length: 14 }, (_, i) => `${String(10 + i).padStart(2, "0")}:00`);

  return (
    <Modal onClose={onClose} title="Edit Appointment" testid="edit-appt-modal">
      <div className="space-y-3">
        <Sel label="Barber" value={f.barber_id} onChange={(v) => set("barber_id", v)} options={barbers.map((b) => [b.id, b.name])} testid="edit-barber" />
        <Sel label="Service" value={f.service_id} onChange={(v) => set("service_id", v)} options={services.map((s) => [s.id, s.name])} testid="edit-service" />
        <Inp label="Date (YYYY-MM-DD)" value={f.date} onChange={(v) => set("date", v)} testid="edit-date" />
        <Sel label="Time" value={f.time} onChange={(v) => set("time", v)} options={times.map((t) => [t, to12h(t)])} testid="edit-time" />
        <Inp label="Customer Name" value={f.customer_name} onChange={(v) => set("customer_name", v)} testid="edit-name" />
        <Inp label="Phone" value={f.customer_phone} onChange={(v) => set("customer_phone", v)} testid="edit-phone" />
        <Sel label="Payment Status" value={f.payment_status} onChange={(v) => set("payment_status", v)}
          options={[["unpaid", "Unpaid"], ["pending_verification", "Pending Verification"], ["paid", "Paid"]]} testid="edit-pay-status" />
      </div>
      <div className="mt-5 flex gap-2">
        <button onClick={onClose} className="flex-1 rounded-xl border border-white/15 py-2.5 text-sm text-gray-300">Cancel</button>
        <button data-testid="edit-appt-save" onClick={() => onSave(f)} className="flex-1 rounded-xl bg-gold py-2.5 text-sm font-bold text-obsidian-900">Save</button>
      </div>
    </Modal>
  );
}

/* ---------- Barbers ---------- */
function Barbers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/barbers", { params: { all: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    try {
      if (f.id) await api.patch(`/admin/barbers/${f.id}`, f);
      else await api.post("/admin/barbers", f);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this barber?")) return;
    await api.delete(`/admin/barbers/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button data-testid="add-barber-btn" onClick={() => setEditing({ name: "", role: "", experience: "", specialty: "", image_url: "", active: true, order: items.length + 1 })}
          className="flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-sm font-bold text-obsidian-900"><Plus size={15} /> Add Barber</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((b) => (
          <div key={b.id} className="overflow-hidden rounded-2xl border border-white/10 bg-obsidian-card" data-testid={`admin-barber-${b.id}`}>
            <img src={b.image_url} alt={b.name} className="h-40 w-full object-cover" />
            <div className="p-4">
              <div className="font-display text-lg font-bold">{b.name}</div>
              <div className="text-xs text-gold">{b.role}</div>
              <div className="mt-2 flex gap-2">
                <button data-testid={`edit-barber-${b.id}`} onClick={() => setEditing(b)} className="flex-1 rounded-lg border border-white/15 py-1.5 text-xs hover:border-gold hover:text-gold">Edit</button>
                <button data-testid={`delete-barber-${b.id}`} onClick={() => remove(b.id)} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs hover:border-red-500 hover:text-red-400"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Barber" : "Add Barber"} testid="barber-modal">
          <BarberForm value={editing} onChange={setEditing} />
          <div className="mt-5 flex gap-2">
            <button onClick={() => setEditing(null)} className="flex-1 rounded-xl border border-white/15 py-2.5 text-sm text-gray-300">Cancel</button>
            <button data-testid="save-barber-btn" onClick={() => save(editing)} className="flex-1 rounded-xl bg-gold py-2.5 text-sm font-bold text-obsidian-900">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const BarberForm = ({ value, onChange }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <Inp label="Name" value={value.name} onChange={(v) => set("name", v)} testid="barber-name" />
      <Inp label="Role" value={value.role} onChange={(v) => set("role", v)} testid="barber-role" />
      <Inp label="Experience" value={value.experience} onChange={(v) => set("experience", v)} testid="barber-exp" />
      <Inp label="Specialty" value={value.specialty} onChange={(v) => set("specialty", v)} testid="barber-specialty" />
      <Inp label="Image URL" value={value.image_url} onChange={(v) => set("image_url", v)} testid="barber-image" />
    </div>
  );
};

/* ---------- Services ---------- */
function Services() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/services", { params: { all: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    const payload = { ...f, price_usd: Number(f.price_usd), order: Number(f.order) || 0, duration_min: 60 };
    try {
      if (f.id) await api.patch(`/admin/services/${f.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button data-testid="add-service-btn" onClick={() => setEditing({ name: "", description: "", price_usd: 0, active: true, order: items.length + 1 })}
          className="flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-sm font-bold text-obsidian-900"><Plus size={15} /> Add Service</button>
      </div>
      <div className="divide-y divide-white/10 rounded-2xl border border-white/10 bg-obsidian-card">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-4 p-4" data-testid={`admin-service-${s.id}`}>
            <div>
              <div className="font-semibold">{s.name}</div>
              <div className="text-xs text-gray-400">{s.description}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm font-bold text-gold">${s.price_usd}</span>
              <button data-testid={`edit-service-${s.id}`} onClick={() => setEditing(s)} className="rounded-lg border border-white/15 p-1.5 hover:border-gold hover:text-gold"><Pencil size={14} /></button>
              <button data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)} className="rounded-lg border border-white/15 p-1.5 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Service" : "Add Service"} testid="service-modal">
          <div className="space-y-3">
            <Inp label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="service-name" />
            <Inp label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="service-desc" />
            <Inp label="Price (USD)" type="number" value={editing.price_usd} onChange={(v) => setEditing({ ...editing, price_usd: v })} testid="service-price" />
          </div>
          <div className="mt-5 flex gap-2">
            <button onClick={() => setEditing(null)} className="flex-1 rounded-xl border border-white/15 py-2.5 text-sm text-gray-300">Cancel</button>
            <button data-testid="save-service-btn" onClick={() => save(editing)} className="flex-1 rounded-xl bg-gold py-2.5 text-sm font-bold text-obsidian-900">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Hours & Blocks ---------- */
const DAY_KEYS = ["tuesday", "wednesday", "thursday", "friday", "saturday", "sunday", "monday"];
function HoursBlocks() {
  const [settings, setSettings] = useState(null);
  const [barbers, setBarbers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: "", time: "10:00", barber_id: "" });
  const [newBlockedDate, setNewBlockedDate] = useState("");

  const load = () => {
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/admin/blocked-slots").then((r) => setBlocks(r.data));
  };
  useEffect(() => { load(); api.get("/barbers", { params: { all: true } }).then((r) => setBarbers(r.data)); }, []);

  const saveHours = async (wh) => {
    setSettings((s) => ({ ...s, working_hours: wh }));
    try { await api.patch("/admin/settings", { working_hours: wh }); toast.success("Hours updated"); }
    catch (e) { toast.error(errText(e)); }
  };
  const toggleClosed = (day) => {
    const wh = { ...settings.working_hours, [day]: { ...settings.working_hours[day], closed: !settings.working_hours[day].closed } };
    saveHours(wh);
  };
  const setHour = (day, field, val) => {
    const wh = { ...settings.working_hours, [day]: { ...settings.working_hours[day], [field]: val } };
    saveHours(wh);
  };

  const addBlockedDate = async () => {
    if (!newBlockedDate) return;
    const arr = [...(settings.blocked_dates || []), newBlockedDate];
    await api.patch("/admin/settings", { blocked_dates: arr });
    setNewBlockedDate(""); toast.success("Date blocked"); load();
  };
  const removeBlockedDate = async (d) => {
    const arr = (settings.blocked_dates || []).filter((x) => x !== d);
    await api.patch("/admin/settings", { blocked_dates: arr }); toast.success("Removed"); load();
  };
  const addBlockSlot = async () => {
    if (!newBlock.date) { toast.error("Pick a date"); return; }
    await api.post("/admin/blocked-slots", { ...newBlock, barber_id: newBlock.barber_id || null });
    toast.success("Slot blocked"); setNewBlock({ date: "", time: "10:00", barber_id: "" }); load();
  };
  const removeBlockSlot = async (id) => { await api.delete(`/admin/blocked-slots/${id}`); load(); };

  if (!settings) return <div className="py-10 text-center text-gold"><Loader2 className="mx-auto animate-spin" /></div>;
  const times = Array.from({ length: 14 }, (_, i) => `${String(10 + i).padStart(2, "0")}:00`);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Working Hours">
        <div className="space-y-2">
          {DAY_KEYS.map((day) => {
            const v = settings.working_hours[day];
            return (
              <div key={day} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2.5" data-testid={`hours-${day}`}>
                <span className="w-24 text-sm capitalize">{day}</span>
                {v.closed ? (
                  <span className="flex-1 text-sm text-gray-500">Closed</span>
                ) : (
                  <div className="flex flex-1 items-center gap-2 font-mono text-xs">
                    <input value={v.open} onChange={(e) => setHour(day, "open", e.target.value)} className="w-16 rounded-md border border-white/10 bg-obsidian-900 px-2 py-1 text-center" data-testid={`open-${day}`} />
                    <span className="text-gray-500">–</span>
                    <input value={v.close} onChange={(e) => setHour(day, "close", e.target.value)} className="w-16 rounded-md border border-white/10 bg-obsidian-900 px-2 py-1 text-center" data-testid={`close-${day}`} />
                  </div>
                )}
                <button onClick={() => toggleClosed(day)} data-testid={`toggle-${day}`}
                  className={`rounded-full border px-3 py-1 text-xs ${v.closed ? "border-green-500/40 text-green-400" : "border-red-500/40 text-red-400"}`}>
                  {v.closed ? "Open" : "Close"}
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-gray-500">Use 24-hour format. Midnight = 24:00.</p>
      </Panel>

      <div className="space-y-6">
        <Panel title="Blocked Dates (full day)">
          <div className="flex gap-2">
            <input type="date" value={newBlockedDate} onChange={(e) => setNewBlockedDate(e.target.value)} data-testid="blocked-date-input"
              className="flex-1 rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2 text-sm outline-none focus:border-gold" />
            <button onClick={addBlockedDate} data-testid="add-blocked-date" className="rounded-xl bg-gold px-4 text-sm font-bold text-obsidian-900">Block</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(settings.blocked_dates || []).map((d) => (
              <span key={d} className="flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/5 px-3 py-1 text-xs text-red-300" data-testid={`blocked-date-${d}`}>
                {prettyDate(d)} <button onClick={() => removeBlockedDate(d)}><X size={12} /></button>
              </span>
            ))}
            {(settings.blocked_dates || []).length === 0 && <span className="text-xs text-gray-500">No blocked dates.</span>}
          </div>
        </Panel>

        <Panel title="Block Specific Slots">
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} data-testid="block-slot-date"
              className="rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2 text-sm outline-none focus:border-gold" />
            <select value={newBlock.time} onChange={(e) => setNewBlock({ ...newBlock, time: e.target.value })} data-testid="block-slot-time"
              className="rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2 text-sm outline-none focus:border-gold">
              {times.map((t) => <option key={t} value={t}>{to12h(t)}</option>)}
            </select>
            <select value={newBlock.barber_id} onChange={(e) => setNewBlock({ ...newBlock, barber_id: e.target.value })} data-testid="block-slot-barber"
              className="col-span-2 rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2 text-sm outline-none focus:border-gold">
              <option value="">All barbers</option>
              {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <button onClick={addBlockSlot} data-testid="add-block-slot" className="mt-2 w-full rounded-xl bg-gold py-2 text-sm font-bold text-obsidian-900">Block Slot</button>
          <div className="mt-3 space-y-2">
            {blocks.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-obsidian-800 px-3 py-2 text-xs" data-testid={`block-slot-${b.id}`}>
                <span>{prettyDate(b.date)} · {to12h(b.time)} · {barbers.find((x) => x.id === b.barber_id)?.name || "All"}</span>
                <button onClick={() => removeBlockSlot(b.id)} className="text-red-400"><Trash2 size={13} /></button>
              </div>
            ))}
            {blocks.length === 0 && <span className="text-xs text-gray-500">No blocked slots.</span>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    try {
      await api.patch("/admin/settings", {
        exchange_rate: Number(s.exchange_rate),
        whish_number: s.whish_number,
        contact: s.contact,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(errText(e)); }
  };
  if (!s) return <div className="py-10 text-center text-gold"><Loader2 className="mx-auto animate-spin" /></div>;
  const setC = (k, v) => setS({ ...s, contact: { ...s.contact, [k]: v } });

  return (
    <div className="max-w-xl space-y-6">
      <Panel title="Payment & Currency">
        <div className="space-y-3">
          <Inp label="Exchange Rate (1 USD = ? LBP)" type="number" value={s.exchange_rate} onChange={(v) => setS({ ...s, exchange_rate: v })} testid="set-rate" />
          <Inp label="Whish Money Number" value={s.whish_number} onChange={(v) => setS({ ...s, whish_number: v })} testid="set-whish" />
        </div>
      </Panel>
      <Panel title="Contact Info">
        <div className="space-y-3">
          <Inp label="Phone" value={s.contact?.phone} onChange={(v) => setC("phone", v)} testid="set-phone" />
          <Inp label="WhatsApp (digits only)" value={s.contact?.whatsapp} onChange={(v) => setC("whatsapp", v)} testid="set-whatsapp" />
          <Inp label="Instagram handle" value={s.contact?.instagram} onChange={(v) => setC("instagram", v)} testid="set-instagram" />
          <Inp label="Address" value={s.contact?.address} onChange={(v) => setC("address", v)} testid="set-address" />
          <Inp label="Google Maps Embed URL" value={s.contact?.map_embed} onChange={(v) => setC("map_embed", v)} testid="set-map" />
        </div>
      </Panel>
      <button data-testid="save-settings-btn" onClick={save} className="w-full rounded-xl bg-gold py-3 font-bold text-obsidian-900">Save Settings</button>
    </div>
  );
}

/* ---------- Shared UI ---------- */
const Panel = ({ title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-obsidian-card p-5">
    <h3 className="mb-4 font-display text-lg font-bold text-white">{title}</h3>
    {children}
  </div>
);

const Inp = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gold">{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2.5 text-sm text-white outline-none focus:border-gold" />
  </div>
);

const Sel = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gold">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-xl border border-white/10 bg-obsidian-800 px-3 py-2.5 text-sm text-white outline-none focus:border-gold">
      {options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
    </select>
  </div>
);

const Modal = ({ title, children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
    <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border border-gold/20 bg-obsidian-card p-6 no-scrollbar" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-white">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>
);
